import Phaser from 'phaser';
import {
  getOwnedImprintCount,
  HELL_DEPTH_MAX,
  IMPRINT_IDS,
  IMPRINT_PANEL_BODY_LOCKED,
  IMPRINT_PANEL_BODY_UNLOCKED,
  requiredImprintsToEnterHellDepth,
} from '../game/imprintGate';
import { getHellFloor, REGISTRY_HELL_FLOOR } from '../game/progress';
import { HELL1_ZONE_IDS, isHell1StageCleared } from '../game/hell1Progress';

/** 지옥 거점 대기실 — 출전 전 준비. */
export class HubScene extends Phaser.Scene {
  private overlayNodes: Phaser.GameObjects.GameObject[] = [];

  constructor() {
    super({ key: 'HubScene' });
  }

  create(): void {
    const { width, height } = this.scale;
    this.cameras.main.setBackgroundColor('#221c26');

    if (this.registry.get(REGISTRY_HELL_FLOOR) === undefined) {
      this.registry.set(REGISTRY_HELL_FLOOR, 1);
    }
    const hellFloor = getHellFloor(this.registry);
    const facilitiesUnlocked = hellFloor >= 2;

    this.add
      .rectangle(width / 2, height / 2 - 40, width * 0.72, height * 0.52, 0x2f2736, 0.55)
      .setStrokeStyle(1, 0x4a4158, 0.9);

    this.add
      .text(width / 2, 26, '거점 · 대기실', {
        fontFamily: 'system-ui, "Segoe UI", sans-serif',
        fontSize: '22px',
        color: '#f2eef8',
      })
      .setOrigin(0.5);

    const hubSubtitle =
      hellFloor < 2
        ? '지옥 I — 장비·각인·상점·도감은 제2층 도달 후 이용할 수 있습니다.'
        : `지옥 ${hellFloor}층 거점 — 상점과 도감을 활용해 더 깊은 층을 준비하세요.`;

    this.add
      .text(width / 2, 54, hubSubtitle, {
        fontFamily: 'system-ui, "Segoe UI", sans-serif',
        fontSize: '12px',
        color: '#9b96ae',
        align: 'center',
        wordWrap: { width: width - 48 },
      })
      .setOrigin(0.5);

    if (facilitiesUnlocked) {
      const imprintCnt = getOwnedImprintCount(this.registry);
      const gateHint =
        hellFloor >= HELL_DEPTH_MAX
          ? `각인 보유 ${imprintCnt}/${IMPRINT_IDS.length} · 제9층 — 일곱 각인 슬롯 모두 개방`
          : (() => {
              const nextDepth = hellFloor + 1;
              const needNext = requiredImprintsToEnterHellDepth(nextDepth);
              return `각인 보유 ${imprintCnt}/${IMPRINT_IDS.length} · 제${nextDepth}층 진입 조건: 각인 ${needNext}개 이상`;
            })();
      this.add
        .text(width / 2, 68, gateHint, {
          fontFamily: 'system-ui, "Segoe UI", sans-serif',
          fontSize: '11px',
          color: '#8f89a4',
          align: 'center',
          wordWrap: { width: width - 40 },
        })
        .setOrigin(0.5);
    }

    this.add
      .text(width / 2, facilitiesUnlocked ? 88 : 72, '제1층 · 구역 포털 (구역 안에서 5단계 연속)', {
        fontFamily: 'system-ui, "Segoe UI", sans-serif',
        fontSize: '13px',
        color: '#c5bfd8',
      })
      .setOrigin(0.5);

    const portalRowY = facilitiesUnlocked ? 114 : 100;
    const zoneId = HELL1_ZONE_IDS[0];
    const cleared = isHell1StageCleared(this.registry, zoneId);
    this.addHell1ZonePortal(width / 2, portalRowY, zoneId, cleared);

    const avatar = this.add.rectangle(width / 2, height / 2 - 6, 40, 40, 0x6bcf7f);
    this.tweens.add({
      targets: avatar,
      y: avatar.y - 8,
      duration: 1500,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    this.add
      .text(width / 2, height / 2 + 62, '대기 중', {
        fontFamily: 'system-ui, "Segoe UI", sans-serif',
        fontSize: '14px',
        color: '#c4bfd6',
      })
      .setOrigin(0.5);

    const activeBtn = {
      fontFamily: 'system-ui, "Segoe UI", sans-serif',
      fontSize: '13px',
      color: '#e6e2f0',
      backgroundColor: '#3d3548cc',
      padding: { left: 12, right: 12, top: 9, bottom: 9 },
    };

    const lockedBtn = {
      fontFamily: 'system-ui, "Segoe UI", sans-serif',
      fontSize: '13px',
      color: '#6d657c',
      backgroundColor: '#282333cc',
      padding: { left: 12, right: 12, top: 9, bottom: 9 },
    };

    const rowFacilitiesY = height - 108;
    const cols = [width * 0.14, width * 0.38, width * 0.62, width * 0.86];

    this.addFacilityButton(cols[0], rowFacilitiesY, '장비', activeBtn, lockedBtn, facilitiesUnlocked, () =>
      this.openPanel(
        '장비',
        '무기·방어구·액세서리 슬롯을 관리합니다.\n던전 전리품과 강화는 이후 단계에서 연결합니다.',
      ),
    );

    this.addFacilityButton(cols[1], rowFacilitiesY, '각인', activeBtn, lockedBtn, facilitiesUnlocked, () =>
      this.openPanel('죄악의 각인', facilitiesUnlocked ? IMPRINT_PANEL_BODY_UNLOCKED : IMPRINT_PANEL_BODY_LOCKED),
    );

    this.addFacilityButton(cols[2], rowFacilitiesY, '상점', activeBtn, lockedBtn, facilitiesUnlocked, () =>
      this.openPanel('상점', '영혼 조각·재화로 물약과 장비를 거래합니다.\n재화와 진열 목록은 이후 단계에서 연결합니다.'),
    );

    this.addFacilityButton(cols[3], rowFacilitiesY, '도감', activeBtn, lockedBtn, facilitiesUnlocked, () =>
      this.openPanel(
        '도감',
        '처치·포획한 적의 약점·저항·출현 층 정보를 기록합니다.\n도감 페이지와 수집 조건은 이후 단계에서 연결합니다.',
      ),
    );

    const settingsY = height - 54;
    this.addActiveTextButton(width / 2, settingsY, '게임 설정', activeBtn, () =>
      this.openPanel('게임 설정', '사운드·언어·입력·화면 배율 등은 이후 단계에서 연결합니다.'),
    );
  }

  private addHell1ZonePortal(x: number, y: number, zoneId: string, cleared: boolean): void {
    const label = cleared
      ? `구역 ${zoneId}\n나선 · 5단계\n(통과)`
      : `구역 ${zoneId}\n나선 · 5단계`;

    const chip = this.add
      .text(x, y, label, {
        fontFamily: 'system-ui, "Segoe UI", sans-serif',
        fontSize: '13px',
        color: '#eae8ff',
        backgroundColor: cleared ? '#2a4540cc' : '#3f3558cc',
        align: 'center',
        lineSpacing: 5,
        padding: { left: 18, right: 18, top: 14, bottom: 14 },
      })
      .setOrigin(0.5)
      .setDepth(55);

    chip.setAlpha(0.96);
    chip.setInteractive({ useHandCursor: true });
    chip.on('pointerover', () => chip.setAlpha(1));
    chip.on('pointerout', () => chip.setAlpha(0.96));
    chip.on('pointerdown', () => this.scene.start('Hell1CombatScene', { stageId: zoneId }));
  }

  private addFacilityButton(
    x: number,
    y: number,
    label: string,
    activeStyle: Phaser.Types.GameObjects.Text.TextStyle,
    lockedStyle: Phaser.Types.GameObjects.Text.TextStyle,
    unlocked: boolean,
    onOpen: () => void,
  ): void {
    const style = unlocked ? activeStyle : lockedStyle;
    const t = this.add
      .text(x, y, label, style)
      .setOrigin(0.5)
      .setDepth(50);

    if (unlocked) {
      t.setInteractive({ useHandCursor: true });
      t.setAlpha(0.94);
      t.on('pointerover', () => t.setAlpha(1));
      t.on('pointerout', () => t.setAlpha(0.94));
      t.on('pointerdown', onOpen);
      return;
    }

    t.setAlpha(0.68);

    const zone = this.add
      .rectangle(x, y, 100, 40, 0x000000, 0.001)
      .setDepth(52)
      .setInteractive({ useHandCursor: true });

    zone.on('pointerdown', () =>
      this.showToast('장비·각인·상점·도감은 지옥 제2층 도달 후 이용할 수 있습니다.'),
    );
  }

  private addActiveTextButton(
    x: number,
    y: number,
    label: string,
    style: Phaser.Types.GameObjects.Text.TextStyle,
    fn: () => void,
  ): void {
    const t = this.add
      .text(x, y, label, style)
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true })
      .setDepth(50);

    t.on('pointerover', () => t.setAlpha(1));
    t.on('pointerout', () => t.setAlpha(0.94));
    t.on('pointerdown', fn);
    t.setAlpha(0.94);
  }

  private showToast(msg: string): void {
    const { width, height } = this.scale;
    const toast = this.add
      .text(width / 2, height - 132, msg, {
        fontFamily: 'system-ui, "Segoe UI", sans-serif',
        fontSize: '13px',
        color: '#e8e2fa',
        backgroundColor: '#1f182bee',
        padding: { left: 16, right: 16, top: 10, bottom: 10 },
        align: 'center',
        wordWrap: { width: width - 56 },
      })
      .setOrigin(0.5)
      .setDepth(360)
      .setAlpha(0);

    this.tweens.add({
      targets: toast,
      alpha: 1,
      duration: 140,
      onComplete: () => {
        this.time.delayedCall(2100, () => {
          this.tweens.add({
            targets: toast,
            alpha: 0,
            duration: 220,
            onComplete: () => toast.destroy(),
          });
        });
      },
    });
  }

  private dismissOverlay(): void {
    this.overlayNodes.forEach((n) => n.destroy());
    this.overlayNodes = [];
  }

  private openPanel(title: string, body: string): void {
    this.dismissOverlay();
    const { width, height } = this.scale;
    const depth = 400;

    const backdrop = this.add
      .rectangle(width / 2, height / 2, width + 4, height + 4, 0x050308, 0.72)
      .setInteractive()
      .setDepth(depth);

    const panelW = Math.min(440, width - 48);
    const panelH = 240;
    const panel = this.add
      .rectangle(width / 2, height / 2, panelW, panelH, 0x322a3d, 1)
      .setStrokeStyle(1, 0x5c5268)
      .setDepth(depth + 1);

    panel.setInteractive();
    panel.on('pointerdown', () => {
      /* 패널 영역 클릭은 배경으로 새지 않도록 흡수 */
    });

    const titleText = this.add
      .text(width / 2, height / 2 - 78, title, {
        fontFamily: 'system-ui, "Segoe UI", sans-serif',
        fontSize: '22px',
        color: '#faf7ff',
      })
      .setOrigin(0.5)
      .setDepth(depth + 2);

    const bodyText = this.add
      .text(width / 2, height / 2 - 14, body, {
        fontFamily: 'system-ui, "Segoe UI", sans-serif',
        fontSize: '14px',
        color: '#cdc8de',
        align: 'center',
        lineSpacing: 5,
        wordWrap: { width: panelW - 48 },
      })
      .setOrigin(0.5)
      .setDepth(depth + 2);

    const closeBtn = this.add
      .text(width / 2, height / 2 + 84, '닫기', {
        fontFamily: 'system-ui, "Segoe UI", sans-serif',
        fontSize: '15px',
        color: '#eae6f5',
        backgroundColor: '#4a415dcc',
        padding: { left: 22, right: 22, top: 10, bottom: 10 },
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true })
      .setDepth(depth + 2);

    closeBtn.on('pointerdown', () => this.dismissOverlay());

    backdrop.on('pointerdown', () => this.dismissOverlay());

    this.overlayNodes.push(backdrop, panel, titleText, bodyText, closeBtn);
  }
}
